import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-hot-toast';
import { axiosInstance } from '../../../../utils/tokenUtils';
import PlatformChargesDonutChart from './PlatformChargesDonutChart';
import './PlatformCreativeArts.css';

// Convert koins to riyals using dynamic conversion rate
const koinsToRiyals = (koins, conversionRate) => {
  return (parseFloat(koins) * conversionRate).toFixed(2);
};

const PlatformCreativeArts = ({
  open,
  onClose,
  sessionData,
  onSaveSuccess,
  conversionRate = 7.5,
}) => {
  const [breakdown, setBreakdown] = useState({
    klassCommission: { koins: 0, percentage: 40 },
    serviceFee: { koins: 0, percentage: 20 },
    instructorCommission: { koins: 0, percentage: 40 },
  });
  const [isLoading, setIsLoading] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');
  const [validationType, setValidationType] = useState('');
  const [feeRange, setFeeRange] = useState(null);
  const [isLoadingFeeRange, setIsLoadingFeeRange] = useState(false);

  // Fetch fee range from API
  const fetchFeeRange = async () => {
    try {
      setIsLoadingFeeRange(true);
      const response = await axiosInstance.get('/admin/creative-art-fee-range');
      if (response.status === 200) {
        setFeeRange(response.data);
      }
    } catch (error) {
      console.error('Error fetching fee range:', error);
      toast.error('Failed to fetch fee range. Using default values.');
    } finally {
      setIsLoadingFeeRange(false);
    }
  };

  // Fetch fee setting details by id and prefill breakdown
  const fetchFeeDetails = async feeSettingsId => {
    if (!feeSettingsId) return;
    try {
      const response = await axiosInstance.get(`/admin/creative-fee-settings/${feeSettingsId}`);
      if (response.status === 200 && response.data) {
        const d = response.data;
        setBreakdown({
          klassCommission: {
            minKoins: parseFloat(d.klass_commission_min_koins || 0),
            maxKoins: parseFloat(d.klass_commission_max_koins || 0),
            minRiyals: parseFloat(d.klass_commission_min_riyals || 0).toFixed(2),
            maxRiyals: parseFloat(d.klass_commission_max_riyals || 0).toFixed(2),
            percentage: parseFloat(d.klass_commission_percentage || 0),
          },
          serviceFee: {
            minKoins: parseFloat(d.service_fee_min_koins || 0),
            maxKoins: parseFloat(d.service_fee_max_koins || 0),
            minRiyals: parseFloat(d.service_fee_min_riyals || 0).toFixed(2),
            maxRiyals: parseFloat(d.service_fee_max_riyals || 0).toFixed(2),
            percentage: parseFloat(d.service_fee_percentage || 0),
          },
          instructorCommission: {
            minKoins: parseFloat(d.instructor_commission_min_koins || 0),
            maxKoins: parseFloat(d.instructor_commission_max_koins || 0),
            minRiyals: parseFloat(d.instructor_commission_min_riyals || 0).toFixed(2),
            maxRiyals: parseFloat(d.instructor_commission_max_riyals || 0).toFixed(2),
            percentage: parseFloat(d.instructor_commission_percentage || 0),
          },
        });
      }
    } catch (error) {
      console.error('Error fetching fee details:', error);
      toast.error('Failed to fetch fee details.');
    }
  };

  // Fetch fee range when modal opens
  useEffect(() => {
    if (open) {
      fetchFeeRange();
    }
  }, [open]);

  // Fetch details when modal opens
  useEffect(() => {
    if (open && sessionData?.id) {
      fetchFeeDetails(sessionData.id);
    }
  }, [open, sessionData?.id]);

  // Helper function to calculate current sum of editable values
  const getCurrentSum = () => {
    return (
      (breakdown.klassCommission.minKoins || 0) +
      (breakdown.serviceFee.minKoins || 0) +
      (breakdown.instructorCommission.minKoins || 0)
    );
  };

  // Helper function to get original total
  const getOriginalTotal = () => {
    // Use fee range from API instead of calculating from session data
    return feeRange?.min_fee_koins || 0;
  };

  // Helper function to validate breakdown (like PlatfromChargesEdit.jsx validateData)
  const isBreakdownValid = () => {
    const currentSum = getCurrentSum();
    const originalTotal = getOriginalTotal();
    const diff = Math.abs(currentSum - originalTotal);
    return diff <= 0.01;
  };

  // Validate total percentage whenever breakdown changes
  useEffect(() => {
    if (sessionData && feeRange) {
      validateBreakdown();
    }
  }, [breakdown, sessionData, feeRange]);

  // Validate the breakdown
  const validateBreakdown = () => {
    if (!sessionData || !feeRange) return;

    // Calculate the current sum of all editable min koins values
    const currentSum = getCurrentSum();
    const originalTotal = getOriginalTotal();

    // Calculate total percentage
    const totalPercentage =
      parseFloat(breakdown.klassCommission.percentage || 0) +
      parseFloat(breakdown.serviceFee.percentage || 0) +
      parseFloat(breakdown.instructorCommission.percentage || 0);

    // Check if the current sum matches the original total (like PlatfromChargesEdit.jsx)
    const diff = Math.abs(currentSum - originalTotal);

    if (diff <= 0.01) {
      setValidationMessage('Perfect! All values are valid.');
      setValidationType('success');
    } else {
      setValidationMessage(
        `Total breakdown must match the total (${originalTotal.toFixed(2)} Koins). Current sum: ${currentSum.toFixed(2)} Koins. Difference: ${diff.toFixed(2)} Koins`
      );
      setValidationType('error');
    }
  };

  // Handle change in Min Koins
  const handleMinKoinsChange = (field, value) => {
    if (!sessionData || !feeRange) return;

    // Handle empty string or invalid input
    if (value === '' || isNaN(value)) {
      setBreakdown(prev => ({
        ...prev,
        [field]: {
          ...prev[field],
          minKoins: 0,
          percentage: 0,
          maxKoins: 0,
          minRiyals: '0.00',
          maxRiyals: '0.00',
        },
      }));
      return;
    }

    let minKoins = parseFloat(value);

    // Prevent negative numbers - automatically convert to 0
    if (minKoins < 0) {
      minKoins = 0;
    }

    // Use fee range from API instead of calculating from session data
    const totalMinFee = feeRange.min_fee_koins || 0;
    const totalMaxFee = feeRange.max_fee_koins || 0;

    if (totalMinFee === 0) return;

    // Calculate percentage based on the original total (like PlatfromChargesEdit.jsx)
    const newPercentage = totalMinFee > 0 ? ((minKoins / totalMinFee) * 100).toFixed(2) : 0;

    // Calculate max koins based on the same percentage of total max fee
    const maxKoins = ((totalMaxFee * parseFloat(newPercentage)) / 100).toFixed(2);

    // Update the field
    setBreakdown(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        minKoins: minKoins,
        percentage: parseFloat(newPercentage),
        maxKoins: parseFloat(maxKoins),
        minRiyals: koinsToRiyals(minKoins, conversionRate),
        maxRiyals: koinsToRiyals(maxKoins, conversionRate),
      },
    }));
  };

  // Handle wheel events on number inputs to prevent page scrolling
  const handleWheel = e => {
    e.preventDefault();
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);

      // Calculate the current sum of all editable min koins values
      const currentSum = getCurrentSum();
      const originalTotal = getOriginalTotal();

      // Check if the current sum matches the original total (like PlatfromChargesEdit.jsx)
      const diff = Math.abs(currentSum - originalTotal);

      if (diff > 0.01) {
        toast.error(
          `Total breakdown must match the total (${originalTotal.toFixed(2)} Koins). Current sum: ${currentSum.toFixed(2)} Koins`
        );
        return;
      }

      // Prepare payload using editable min koins
      const payload = {
        klass_commission_min_koins: parseFloat(breakdown.klassCommission.minKoins) || 0,
        service_fee_min_koins: parseFloat(breakdown.serviceFee.minKoins) || 0,
        instructor_commission_min_koins: parseFloat(breakdown.instructorCommission.minKoins) || 0,
      };

      const response = await axiosInstance.patch(
        `/admin/creative-fee-settings/${sessionData.id}`,
        payload
      );

      if (response.status === 200) {
        toast.success('Creative Arts session updated successfully');
        onSaveSuccess();
        onClose();
      }
    } catch (error) {
      console.error('Error updating creative arts session:', error);
      // Show server-provided error messages when available
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        if (errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            const firstError = errorData.detail[0];
            if (firstError && firstError.msg) {
              toast.error(firstError.msg);
            } else if (typeof firstError === 'string') {
              toast.error(firstError);
            } else {
              toast.error('Validation error occurred. Please check your input.');
            }
          } else if (typeof errorData.detail === 'string') {
            toast.error(errorData.detail);
          } else {
            toast.error('Failed to update session. Please try again.');
          }
        } else if (errorData.message) {
          toast.error(errorData.message);
        } else if (typeof errorData === 'string') {
          toast.error(errorData);
        } else {
          toast.error('Failed to update session. Please try again.');
        }
      } else if (error.message) {
        toast.error(error.message);
      } else {
        toast.error('Failed to update session. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Prepare data for donut chart
  const chartData = [
    {
      label: 'Klass Commission',
      value: parseFloat(breakdown.klassCommission.maxKoins || 0),
    },
    {
      label: 'Service/Platform Fee',
      value: parseFloat(breakdown.serviceFee.maxKoins || 0),
    },
    {
      label: 'Instructor Commission',
      value: parseFloat(breakdown.instructorCommission.maxKoins || 0),
    },
  ];

  if (!sessionData) return null;

  // Use fee range from API instead of calculating from session data
  const totalMinFee = feeRange?.min_fee_koins || 0;
  const totalMaxFee = feeRange?.max_fee_koins || 0;

  // Show loading state if fee range is being fetched
  if (isLoadingFeeRange) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        className="creative-arts-dialog"
      >
        <DialogContent className="creative-arts-dialog-content">
          <Box className="creative-arts-header">
            <Typography className="creative-arts-title">{sessionData.type}</Typography>
            <IconButton onClick={onClose} className="creative-arts-close-btn">
              <CloseIcon />
            </IconButton>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px',
              minHeight: '200px',
            }}
          >
            <Typography
              sx={{
                fontSize: '16px',
                color: '#6b7280',
                fontFamily: 'Poppins',
              }}
            >
              Loading fee range data...
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth className="creative-arts-dialog">
      <DialogContent className="creative-arts-dialog-content">
        <Box className="creative-arts-header">
          <Typography className="creative-arts-title">{sessionData.type}</Typography>
          <IconButton onClick={onClose} className="creative-arts-close-btn">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box className="creative-arts-content">
          <Box className="creative-arts-left-section">
            {/* Current Fee Range Display */}
            <Box className="creative-arts-fee-range">
              <Typography className="creative-arts-section-title">Current Fee Range</Typography>
              {isLoadingFeeRange ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '16px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    marginBottom: '16px',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '14px',
                      color: '#6b7280',
                      fontFamily: 'Poppins',
                    }}
                  >
                    Loading fee range...
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    padding: '16px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    marginBottom: '16px',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '14px',
                      color: '#6b7280',
                      fontFamily: 'Poppins',
                    }}
                  >
                    Range:
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontWeight: 600,
                      color: '#111827',
                      fontFamily: 'Poppins',
                    }}
                  >
                    {totalMinFee.toFixed(2)} - {totalMaxFee.toFixed(2)} Koins
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      color: '#6b7280',
                      fontFamily: 'Poppins',
                    }}
                  >
                    ({koinsToRiyals(totalMinFee, conversionRate)} -{' '}
                    {koinsToRiyals(totalMaxFee, conversionRate)} Riyals)
                  </Typography>
                </Box>
              )}
              <Typography
                sx={{
                  fontSize: '12px',
                  color: '#6b7280',
                  fontFamily: 'Poppins',
                  fontStyle: 'italic',
                }}
              >
                Note: Fee range is controlled from the main Creative Arts tab
              </Typography>
            </Box>

            {/* Learner Charges Table */}
            <Box className="creative-arts-table-section">
              <Box className="creative-arts-table">
                <Box className="creative-arts-table-header">
                  <Box className="creative-arts-table-cell">Label</Box>
                  <Box className="creative-arts-table-cell">Mini Koins</Box>
                  <Box className="creative-arts-table-cell">Max Koins</Box>
                  <Box className="creative-arts-table-cell">Min Riyal</Box>
                  <Box className="creative-arts-table-cell">Max Riyal</Box>
                  <Box className="creative-arts-table-cell">%</Box>
                </Box>
                <Box className="creative-arts-table-row">
                  <Box className="creative-arts-table-cell">Learner Charges</Box>
                  <Box className="creative-arts-table-cell">
                    <span className="number-value">{totalMinFee.toFixed(2)}</span>
                  </Box>
                  <Box className="creative-arts-table-cell">
                    <span className="number-value">{totalMaxFee.toFixed(2)}</span>
                  </Box>
                  <Box className="creative-arts-table-cell">
                    <span className="number-value">
                      {koinsToRiyals(totalMinFee, conversionRate)}
                    </span>
                  </Box>
                  <Box className="creative-arts-table-cell">
                    <span className="number-value">
                      {koinsToRiyals(totalMaxFee, conversionRate)}
                    </span>
                  </Box>
                  <Box className="creative-arts-table-cell">
                    <span className="number-value">100%</span>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Breakdown Section */}
            <Box className="creative-arts-breakdown-section">
              <Typography className="creative-arts-section-title">Breakdown</Typography>
              <Box className="creative-arts-breakdown-table">
                <Box className="creative-arts-table-header">
                  <Box className="creative-arts-table-cell">Label</Box>
                  <Box className="creative-arts-table-cell">Mini Koins</Box>
                  <Box className="creative-arts-table-cell">Max Koins</Box>
                  <Box className="creative-arts-table-cell">Min Riyal</Box>
                  <Box className="creative-arts-table-cell">Max Riyal</Box>
                  <Box className="creative-arts-table-cell">%</Box>
                </Box>

                <Box className="creative-arts-table-row">
                  <Box className="creative-arts-table-cell">Klass Commission</Box>
                  <Box className="creative-arts-table-cell">
                    <TextField
                      type="number"
                      value={
                        breakdown.klassCommission.minKoins === 0
                          ? '0'
                          : breakdown.klassCommission.minKoins || ''
                      }
                      onChange={e => handleMinKoinsChange('klassCommission', e.target.value)}
                      className="koins-input"
                      size="small"
                      disabled={!feeRange}
                      inputProps={{ min: 0, max: totalMinFee, step: '0.01' }}
                      onWheel={handleWheel}
                      onKeyDown={e => {
                        // Prevent negative sign
                        if (e.key === '-') {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Box>
                  <Box className="creative-arts-table-cell">
                    <span className="number-value">{breakdown.klassCommission.maxKoins}</span>
                  </Box>
                  <Box className="creative-arts-table-cell">
                    <span className="number-value">{breakdown.klassCommission.minRiyals}</span>
                  </Box>
                  <Box className="creative-arts-table-cell">
                    <span className="number-value">{breakdown.klassCommission.maxRiyals}</span>
                  </Box>
                  <Box className="creative-arts-table-cell">
                    <span className="number-value">
                      {parseFloat(breakdown.klassCommission.percentage).toFixed(2)}%
                    </span>
                  </Box>
                </Box>

                <Box className="creative-arts-table-row">
                  <Box className="creative-arts-table-cell">Service/Platform Fee</Box>
                  <Box className="creative-arts-table-cell">
                    <TextField
                      type="number"
                      value={
                        breakdown.serviceFee.minKoins === 0
                          ? '0'
                          : breakdown.serviceFee.minKoins || ''
                      }
                      onChange={e => handleMinKoinsChange('serviceFee', e.target.value)}
                      className="koins-input"
                      size="small"
                      disabled={!feeRange}
                      inputProps={{ min: 0, max: totalMinFee, step: '0.01' }}
                      onWheel={handleWheel}
                      onKeyDown={e => {
                        // Prevent negative sign
                        if (e.key === '-') {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Box>
                  <Box className="creative-arts-table-cell">
                    <span className="number-value">{breakdown.serviceFee.maxKoins}</span>
                  </Box>
                  <Box className="creative-arts-table-cell">
                    <span className="number-value">{breakdown.serviceFee.minRiyals}</span>
                  </Box>
                  <Box className="creative-arts-table-cell">
                    <span className="number-value">{breakdown.serviceFee.maxRiyals}</span>
                  </Box>
                  <Box className="creative-arts-table-cell">
                    <span className="number-value">
                      {parseFloat(breakdown.serviceFee.percentage).toFixed(2)}%
                    </span>
                  </Box>
                </Box>

                <Box className="creative-arts-table-row">
                  <Box className="creative-arts-table-cell">Instructor Commission</Box>
                  <Box className="creative-arts-table-cell">
                    <TextField
                      type="number"
                      value={
                        breakdown.instructorCommission.minKoins === 0
                          ? '0'
                          : breakdown.instructorCommission.minKoins || ''
                      }
                      onChange={e => handleMinKoinsChange('instructorCommission', e.target.value)}
                      className="koins-input"
                      size="small"
                      disabled={!feeRange}
                      inputProps={{ min: 0, max: totalMinFee, step: '0.01' }}
                      onWheel={handleWheel}
                      onKeyDown={e => {
                        // Prevent negative sign
                        if (e.key === '-') {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Box>
                  <Box className="creative-arts-table-cell">
                    <span className="number-value">{breakdown.instructorCommission.maxKoins}</span>
                  </Box>
                  <Box className="creative-arts-table-cell">
                    <span className="number-value">{breakdown.instructorCommission.minRiyals}</span>
                  </Box>
                  <Box className="creative-arts-table-cell">
                    <span className="number-value">{breakdown.instructorCommission.maxRiyals}</span>
                  </Box>
                  <Box className="creative-arts-table-cell">
                    <span className="number-value">
                      {parseFloat(breakdown.instructorCommission.percentage).toFixed(2)}%
                    </span>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Validation Message */}
            {validationMessage && (
              <Box
                className={`creative-arts-validation ${validationType === 'success' ? 'success' : 'error'}`}
              >
                {validationMessage}
              </Box>
            )}
          </Box>

          {/* Right Section - Donut Chart */}
          <Box className="creative-arts-right-section">
            <PlatformChargesDonutChart data={chartData} centerValue={totalMaxFee} />
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box className="creative-arts-actions">
          <Button onClick={onClose} className="creative-arts-cancel-btn" disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            className="creative-arts-save-btn"
            disabled={isLoading || !isBreakdownValid() || !feeRange}
          >
            {isLoading ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PlatformCreativeArts;
